import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Headphones, Plus, Trash2, User } from 'lucide-react';

const SuperAdminSupportAgents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', title: 'Kundtjänst', password: '', avatar_url: '' });

  const fetchAgents = async () => {
    try {
      const res = await api.superadminGetSupportAgents();
      setAgents(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAgents(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email) { toast.error('Fyll i namn och e-post'); return; }
    try {
      await api.superadminCreateSupportAgent(form);
      toast.success(`${form.name} tillagd som kundtjänst`);
      setShowCreate(false);
      setForm({ name: '', email: '', title: 'Kundtjänst', password: '', avatar_url: '' });
      fetchAgents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunde inte skapa agent'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Vill du ta bort ${name}?`)) return;
    try {
      await api.superadminDeleteSupportAgent(id);
      toast.success('Agent borttagen');
      fetchAgents();
    } catch (err) { toast.error('Kunde inte ta bort agent'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="sa-support-agents-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">Kundtjänst</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Hantera medarbetare som svarar i live chatten</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="sa-add-agent-btn">
          <Plus size={16} className="mr-1.5" /> Ny medarbetare
        </Button>
      </div>

      {agents.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-10 text-center">
          <Headphones size={36} className="mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-medium text-foreground">Inga kundtjänstmedarbetare</p>
          <p className="text-sm text-muted-foreground mt-1">Skapa ett kundtjänstkonto för att börja svara kunder i live chatten.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {agents.map(agent => (
            <div key={agent.id} className="bg-card border border-border rounded-xl p-5 flex items-start gap-4" data-testid={`sa-agent-${agent.id}`}>
              <div className="flex-shrink-0">
                {agent.avatar_url ? (
                  <img src={agent.avatar_url} alt={agent.name} className="w-14 h-14 rounded-full object-cover border-2 border-border" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#E8613A]/10 flex items-center justify-center border-2 border-border">
                    <span className="text-xl font-bold text-[#E8613A]">{agent.name.charAt(0)}</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-foreground">{agent.name}</p>
                <p className="text-xs text-[#E8613A] font-medium">{agent.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{agent.email}</p>
              </div>
              <button onClick={() => handleDelete(agent.id, agent.name)} className="p-2 text-muted-foreground hover:text-red-500 transition-colors" data-testid={`sa-delete-agent-${agent.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create Agent Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ny kundtjänstmedarbetare</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Namn</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Anna Svensson" className="mt-1" data-testid="sa-agent-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">E-post</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="anna@operio.se" className="mt-1" data-testid="sa-agent-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Titel</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Kundtjänst" className="mt-1" data-testid="sa-agent-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Lösenord</Label>
              <Input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="mt-1" data-testid="sa-agent-password" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bild-URL (valfritt)</Label>
              <Input value={form.avatar_url} onChange={e => setForm(f => ({ ...f, avatar_url: e.target.value }))} placeholder="https://..." className="mt-1" data-testid="sa-agent-avatar" />
              <p className="text-[10px] text-muted-foreground mt-1">Bilden visas för kunder i chatten för en personlig känsla</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Avbryt</Button>
            <Button onClick={handleCreate} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="sa-create-agent-submit">Skapa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminSupportAgents;
